import { useEffect, useState } from "react";
import { Gauge, Info } from "lucide-react";
import { api } from "@/lib/api";
import { Widget } from "@/components/dashboard/Widget";
import { HealthBar } from "@/components/dashboard/HealthBar";
import { TrendPill } from "@/components/dashboard/TrendPill";
import {
  Tooltip, TooltipContent, TooltipTrigger, TooltipProvider,
} from "@/components/ui/tooltip";

/** Interactive Career Score Breakdown — 7 sub-scores with hover explanations. */
export const CareerScoreBreakdownWidget = ({ size = "lg" }) => {
  const [state, setState] = useState({ loading: true, data: null, error: false });

  useEffect(() => {
    let alive = true;
    api.get("/dashboard/health")
      .then((r) => { if (alive) setState({ loading: false, data: r.data, error: false }); })
      .catch(() => { if (alive) setState({ loading: false, data: null, error: true }); });
    return () => { alive = false; };
  }, []);

  const score = state.data?.career_score;
  const trend = state.data?.career_score_trend;

  return (
    <Widget
      title="Career Score Breakdown"
      icon={Gauge}
      size={size}
      testId="career-score-breakdown-widget"
    >
      {state.loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
          {Array.from({ length: 7 }).map((_, i) => (
            <div key={i} className="space-y-1.5">
              <div className="skeleton h-3 w-24" />
              <div className="skeleton h-1.5 w-full" />
            </div>
          ))}
        </div>
      )}
      {state.error && (
        <div className="text-sm text-muted-foreground text-center py-4">
          Couldn&apos;t load your Career Score.
        </div>
      )}
      {state.data && (
        <>
          <div className="flex items-center justify-between mb-4 pb-4 border-b border-border">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">
                Career Score
              </div>
              <div className="flex items-baseline gap-2">
                <span className="font-display font-black text-3xl text-foreground" data-testid="career-score-value">
                  {score ?? 0}
                </span>
                <span className="text-sm text-muted-foreground">/ 100</span>
              </div>
            </div>
            {trend && (
              <TrendPill
                delta={trend.delta}
                period={trend.period_days ? `${trend.period_days}d` : undefined}
                testId="career-score-trend"
              />
            )}
          </div>
          <TooltipProvider delayDuration={150}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3.5">
              {state.data.score_breakdown.map((b) => (
                <div key={b.key} className="relative">
                  <HealthBar
                    label={
                      <span className="inline-flex items-center gap-1">
                        {b.label}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              aria-label={`Why ${b.label}?`}
                              className="text-muted-foreground/60 hover:text-foreground transition-colors"
                            >
                              <Info size={10} strokeWidth={2} />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-xs text-xs">
                            {b.explain}
                          </TooltipContent>
                        </Tooltip>
                      </span>
                    }
                    value={b.value}
                    state={(b.value === 0 || b.value == null) && b.unlock_action ? "missing" : "ok"}
                    unlockAction={b.unlock_action}
                    trend={b.trend}
                    explain={b.explain}
                    compact
                    testId={`score-${b.key}`}
                  />
                </div>
              ))}
            </div>
          </TooltipProvider>
        </>
      )}
    </Widget>
  );
};
